import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, Button, VStack, HStack, Text, Portal, IconButton, Tooltip, Divider, Icon, useColorMode,
} from '@chakra-ui/react';
import {
  FiActivity, FiTrendingUp, FiHeart, FiThermometer, FiDroplet, FiZap, FiClipboard,
  FiBarChart2, FiPrinter, FiSettings, FiGrid, FiUser, FiUsers, FiCalendar, FiFolder,
  FiHome, FiPlus, FiList, FiAlertTriangle, FiFileText, FiX,
} from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useHideToEdge } from '../hooks/useHideToEdge';
import { RESET_FLOATING_PANELS } from '../hooks/floatingPanels';
import {
  LAUNCHER, menuMaxH, LauncherEdgeTab, LauncherHideButton, useLauncherClickOutside,
  launcherFabMotion, useLauncherReducedMotion,
} from './floating/LauncherChrome';
import { getColor } from '../brandConfig';
import { ModuleConfig } from '../types/ModuleConfig';
import { useAuth } from '../contexts/AuthContext';
import { useModulePermissions } from '../contexts/ModulePermissionsContext';
import { useShiftKey, isDevMode } from '../hooks/useShiftKey';
import { renderPermissionTooltip } from './devPermissionTooltip';

/**
 * ModuleNavLauncher — the module's own navigation, as a draggable floating button.
 *
 * This REPLACES `ModuleBreadcrumb`, the sticky bar that used to sit under the app navbar on
 * every module page. The bar cost a strip of screen on every page whether or not the reader
 * wanted to navigate, and on the pages that matter most here — the roster grid, the allocation
 * sheet, a long care plan — that strip is a row of real content.
 *
 * Follows `vault/how-to/draggable-floating-launchers.md` § "Module-nav launcher": drag to
 * reposition with per-user localStorage persistence, `wasJustDragged()` guarding the click, the
 * panel placed by `computeMenuPlacement` inside its own Portal at viewport coordinates so it can
 * never open off-screen, and every size taken from `LauncherChrome` (2026-08-27) — the FAB, the
 * menu width, the 44px rows, the mobile-responsive hide tab — so this launcher cannot drift
 * below the standard the way the 52px / 300px / `sm`-rows first cut did.
 *
 * Everything about WHO SEES WHAT is carried over verbatim from the breadcrumb it replaces — the
 * same `permissionLevel` → backend resolution, the same no-ADMIN-bypass rule, and the same
 * shift-held dev permission tooltip. This moves where navigation lives, not who can reach it.
 *
 * Home is the LEFT edge, vertically centred (doc § Default corners): the app's `FloatingNavbar`
 * owns the bottom-left (desktop) / bottom-right (phones) corners and Quick Note + Tom AI the
 * bottom-right, and two launchers stacked on one corner is the exact problem the draggable
 * standard was written to stop. A fixed top offset would only work by luck — it assumes a
 * navbar height that varies by viewport and by whether the navbar wraps.
 */

const iconMap: Record<string, any> = {
  FiActivity, FiTrendingUp, FiHeart, FiThermometer, FiDroplet, FiZap, FiClipboard,
  FiBarChart2, FiPrinter, FiSettings, FiGrid, FiUser, FiUsers, FiCalendar, FiFolder,
  FiHome, FiPlus, FiList, FiAlertTriangle, FiFileText,
};

const getIconComponent = (iconName?: string) => (iconName ? iconMap[iconName] || null : FiGrid);

/** An icon string that isn't a react-icons name is an emoji, and renders as text. */
const isEmoji = (str?: string): boolean => (!str ? false : !str.startsWith('Fi') && !iconMap[str]);

const FAB_SIZE = LAUNCHER.FAB_SIZE;

/** The one home position — used by the hook's default AND the reset handler, never re-typed. */
export const moduleNavHome = (_vw: number, vh: number) => ({
  left: 16,
  top: Math.max(LAUNCHER.EDGE_PAD, Math.round(vh / 2 - FAB_SIZE / 2)),
});

// z-index ladder (draggable-floating-launchers.md § Reset-panels broadcast).
// 998 = nav FAB + its edge tab · 999 = nav menu panel. Deliberately below the 1300 immersive
// overlays: a page in full-screen mode covers this launcher on purpose.
const Z_FAB = 998;
const Z_MENU = 999;

const FAB_CLASS = 'module-nav-fab';
const PANEL_CLASS = 'module-nav-panel';

export const ModuleNavLauncher: React.FC<{ moduleConfig: ModuleConfig }> = ({ moduleConfig }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { getPermissions } = useModulePermissions();
  const { colorMode } = useColorMode();
  const shiftHeld = useShiftKey();
  const devReveal = isDevMode() && shiftHeld;

  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const panelBg = colorMode === 'light' ? 'rgba(255,255,255,0.98)' : 'rgba(10,10,15,0.95)';
  const panelBorder = getColor('border.darkCard', colorMode);
  const textPrimary = getColor('text.primaryDark', colorMode);
  const textSecondary = getColor('text.secondaryDark', colorMode);
  const primaryColor = getColor('primary', colorMode);
  const fabColor = (moduleConfig as any).color || primaryColor;

  // Same derivation this repo's FloatingNavbar uses, so both launchers key on the same person.
  const userId = (user as any)?.clientId || (user as any)?.client?.id || (user as any)?.id || 'guest';
  // Per module, per user: someone who lives in the roster parks it differently from where they
  // want it in care plans. Prefix tlp_ here (plcc_ on PLCC, tms_ on master, siphr_ on siphr) —
  // registered in hooks/floatingPanels.ts; never rename a live key.
  const posKey = `tlp_module_nav_pos_${moduleConfig.id}_${userId}`;
  const hiddenKey = `tlp_module_nav_hidden_${moduleConfig.id}_${userId}`;

  const { position, setPosition, dragHandlers, wasJustDragged } = useDraggableFloat({
    storageKey: posKey,
    size: FAB_SIZE,
    edgePad: LAUNCHER.EDGE_PAD,
    defaultPosition: moduleNavHome,
  });
  const { hidden, setHidden, justHidden } = useHideToEdge(hiddenKey);
  const reducedMotion = useLauncherReducedMotion();

  // The reset failsafe. Self-clearing is required: useDraggableFloat only persists on drag-end,
  // so a listener that just moved the button would let the lost position return on reload.
  useEffect(() => {
    const onReset = () => {
      setOpen(false);
      setPosition(moduleNavHome(window.innerWidth, window.innerHeight));
      try {
        localStorage.removeItem(posKey);
        localStorage.removeItem(hiddenKey);
      } catch { /* private browsing */ }
    };
    window.addEventListener(RESET_FLOATING_PANELS, onReset);
    return () => window.removeEventListener(RESET_FLOATING_PANELS, onReset);
  }, [posKey, hiddenKey, setPosition]);

  const close = useCallback(() => setOpen(false), []);
  useLauncherClickOutside(open, [`.${FAB_CLASS}`, `.${PANEL_CLASS}`], close);

  // A route change means they've arrived — get out of the way.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  /**
   * Unchanged from the breadcrumb: resolve `permissionLevel` through the backend when the item
   * hasn't already been resolved by App.tsx. No ADMIN bypass — the backend's `permissions.ts`
   * already lists `ClientPermission.ADMIN` in every array, so an admin matches naturally.
   */
  const hasPermission = (item: { permissions?: string[]; permissionLevel?: string }) => {
    let perms = item.permissions;
    if ((!perms || perms.length === 0) && item.permissionLevel) {
      perms = getPermissions(moduleConfig.id, item.permissionLevel);
    }
    if (!perms || perms.length === 0) return true;
    if (!user?.permissions) return false;
    return perms.some((permission) => user.permissions?.includes(permission));
  };

  const items = (moduleConfig.navigation || []).filter((item) =>
    hasPermission(item as { permissions?: string[]; permissionLevel?: string }));

  if (items.length === 0) return null;

  // Hidden: the edge tab, and nothing else. It is the guarantee that hiding can never strand
  // the user, so it is never gated behind anything.
  if (hidden) {
    return (
      <Portal>
        <LauncherEdgeTab
          announce={justHidden}
          edge="left"
          top={position.top}
          bg={fabColor}
          zIndex={Z_FAB}
          label={`Bring the ${moduleConfig.name} menu back`}
          onClick={() => setHidden(false)}
        />
      </Portal>
    );
  }

  const currentPath = location.pathname;
  const activePath = [...items]
    .sort((a, b) => b.path.length - a.path.length)
    .find((item) => {
      if (item.path === currentPath) return true;
      // /client/:id belongs to /clients, /bill/:id to /bills — carried over from the breadcrumb.
      if (currentPath.match(/^\/client\/[^/]+$/)) return item.path === '/clients';
      if (currentPath.match(/^\/bill\/[^/]+$/)) return item.path === '/bills';
      return currentPath.startsWith(`${item.path}/`);
    })?.path;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const placement = computeMenuPlacement(
    { left: position.left, top: position.top, size: FAB_SIZE },
    { w: vw, h: vh },
    {
      preferredWidth: LAUNCHER.MENU_W,
      estimatedHeight: 104 + items.length * (LAUNCHER.ROW_MIN_H + 2),
      gap: LAUNCHER.MENU_GAP,
    },
    LAUNCHER.EDGE_PAD,
  );

  return (
    <>
      {open && (
        <Portal>
          <Box
            className={PANEL_CLASS}
            position="fixed"
            {...(placement.top !== undefined ? { top: `${placement.top}px` } : {})}
            {...(placement.bottom !== undefined ? { bottom: `${placement.bottom}px` } : {})}
            left={`${placement.left}px`}
            width={`${placement.width}px`}
            zIndex={Z_MENU}
          >
            <Box
              bg={panelBg}
              backdropFilter="blur(20px)"
              borderWidth="1px"
              borderColor={panelBorder}
              borderRadius="2xl"
              boxShadow={LAUNCHER.SHADOW_MENU}
              p={2}
              // Always fully on screen, scrolling INTERNALLY when there is more menu than room;
              // overscrollBehavior stops that inner scroll chaining to the page (2026-08-20 rule).
              maxH={menuMaxH(placement)}
              overflowY="auto"
              overscrollBehavior="contain"
            >
              <HStack px={3} pt={2} pb={2} spacing={2}>
                {isEmoji(moduleConfig.icon)
                  ? <Text fontSize="xl" lineHeight="1">{moduleConfig.icon}</Text>
                  : <Icon as={getIconComponent(moduleConfig.icon) || FiGrid} fontSize="xl" />}
                <Text fontSize="md" fontWeight="bold" color={textPrimary}>{moduleConfig.name}</Text>
              </HStack>
              <Divider borderColor={panelBorder} mb={1} />
              <VStack align="stretch" spacing={0.5}>
                {items.map((item) => {
                  const isActive = activePath === item.path;
                  return (
                    <Tooltip
                      key={item.path}
                      placement="right"
                      hasArrow
                      openDelay={devReveal ? 0 : 600}
                      bg={devReveal ? 'gray.800' : undefined}
                      color={devReveal ? 'white' : undefined}
                      px={devReveal ? 3 : undefined}
                      py={devReveal ? 2 : undefined}
                      label={devReveal
                        ? renderPermissionTooltip({
                            label: item.label,
                            path: item.path,
                            moduleId: moduleConfig.id,
                            permissionLevel: (item as any).permissionLevel,
                            resolved: (item as any).permissionLevel
                              ? getPermissions(moduleConfig.id, (item as any).permissionLevel)
                              : ((item.permissions as string[] | undefined) ?? []),
                          })
                        : `Go to ${item.label}`}
                    >
                      <Button
                        as={Link}
                        to={item.path}
                        size="md"
                        minH={`${LAUNCHER.ROW_MIN_H}px`}
                        variant="ghost"
                        justifyContent="flex-start"
                        fontSize="md"
                        fontWeight={isActive ? 'semibold' : 'normal'}
                        color={isActive ? primaryColor : textSecondary}
                        bg={isActive ? 'rgba(59, 130, 246, 0.12)' : 'transparent'}
                        _hover={{ bg: 'rgba(59, 130, 246, 0.18)', textDecoration: 'none' }}
                        aria-current={isActive ? 'page' : undefined}
                        onClick={() => setOpen(false)}
                        leftIcon={
                          isEmoji(item.icon)
                            ? <Text fontSize="md" lineHeight="1">{item.icon}</Text>
                            : <Icon as={getIconComponent(item.icon) || FiGrid} fontSize="md" />
                        }
                      >
                        {item.label}
                      </Button>
                    </Tooltip>
                  );
                })}
              </VStack>
              {/* No reset row here, deliberately: a reset reachable only by opening the
                  launcher is useless precisely when the launcher is what's lost. Footer. */}
              <Text fontSize="xs" color={textSecondary} px={3} pt={2} opacity={0.8}>
                Drag the button to move it — it stays where you put it.
              </Text>
            </Box>
          </Box>
        </Portal>
      )}

      <Portal>
        <Box
          ref={wrapRef}
          className={FAB_CLASS}
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          zIndex={Z_FAB}
        >
          {/* Hide-to-edge: hugs the FAB, and hiding also closes the menu. */}
          <LauncherHideButton
            fabSize={FAB_SIZE}
            wasJustDragged={wasJustDragged}
            label="Hide this menu — it becomes a small tab on the left edge, and “↻ Reset floating panels” in the footer brings it back"
            onClick={() => { setOpen(false); setHidden(true); }}
          />

          <Tooltip
            hasArrow
            placement="right"
            openDelay={500}
            label={`${moduleConfig.name} menu — drag to move, tap to open`}
          >
            <IconButton
              {...dragHandlers}
              aria-label={`${moduleConfig.name} menu — drag to move, tap to open`}
              aria-expanded={open}
              icon={open
                ? <FiX size={24} />
                : isEmoji(moduleConfig.icon)
                  ? <Text fontSize="2xl" lineHeight="1">{moduleConfig.icon}</Text>
                  : <Icon as={getIconComponent(moduleConfig.icon) || FiGrid} fontSize="2xl" />}
              isRound
              size="lg"
              w={`${FAB_SIZE}px`}
              h={`${FAB_SIZE}px`}
              minW={`${FAB_SIZE}px`}
              {...launcherFabMotion(reducedMotion)}
              bg={fabColor}
              color="white"
              onClick={() => {
                if (wasJustDragged()) return; // a drag must not also count as a tap
                setOpen((v) => !v);
              }}
            />
          </Tooltip>
        </Box>
      </Portal>
    </>
  );
};

export default ModuleNavLauncher;
